import { RetrievalMethod } from '@/feature/profiles/constants';
import { Facesheet } from '@/feature/profiles/types';

export type FacesheetTemplateType = 'FULL' | 'HIE';

export interface FacesheetTemplateCapabilities {
  hasFullTemplate: boolean;
  hasHieTemplate: boolean;
}

export interface FacesheetsValidationResult {
  isValid: boolean;
  errorMessage?: string;
  missingMethods?: RetrievalMethod[];
}

interface ValidateFacesheetsSelectionParams {
  selectedMethods: RetrievalMethod[];
  capabilities: FacesheetTemplateCapabilities;
  allowStandaloneFacesheets?: boolean;
}

interface GetAllowedFacesheetsParams {
  selectedMethods: RetrievalMethod[];
  facesheets: Facesheet[];
  allowStandaloneFacesheets?: boolean;
}

interface IsFacesheetAllowedParams {
  selectedMethods: RetrievalMethod[];
  facesheet: Facesheet;
  allowStandaloneFacesheets?: boolean;
}

export const FACESHEETS_VALIDATION_MESSAGES = {
  FACESHEETS_REQUIRED_FOR_HIE: 'Facesheets are required to retrieve HIE data.',
  HIE_REQUIRED: 'Select Digital Health Data to generate Facesheets.',
  FULL_REQUIRED: 'Select Provider/Facility Retrieval to generate Facesheets.',
  FULL_OR_HIE_REQUIRED:
    'Select Provider/Facility Retrieval or Digital Health Data to generate Facesheets.',
} as const;

const hasTemplateType = (
  facesheet: Facesheet,
  templateType: FacesheetTemplateType,
) =>
  facesheet.templateTypes.some(
    (value) => value.toUpperCase() === templateType.toUpperCase(),
  );

export const deriveFacesheetTemplateCapabilities = (
  facesheets: Facesheet[],
): FacesheetTemplateCapabilities => ({
  hasFullTemplate: facesheets.some((facesheet) =>
    hasTemplateType(facesheet, 'FULL'),
  ),
  hasHieTemplate: facesheets.some((facesheet) =>
    hasTemplateType(facesheet, 'HIE'),
  ),
});

export const validateFacesheetsSelection = ({
  selectedMethods,
  capabilities,
  allowStandaloneFacesheets = false,
}: ValidateFacesheetsSelectionParams): FacesheetsValidationResult => {
  const isFacesheetsSelected = selectedMethods.includes(
    RetrievalMethod.FACESHEETS,
  );
  const isHieSelected = selectedMethods.includes(RetrievalMethod.HIE);
  const isProviderFacilitySelected = selectedMethods.includes(
    RetrievalMethod.PROVIDER_FACILITY,
  );
  const hasRetrievalSelection = isHieSelected || isProviderFacilitySelected;

  if (isHieSelected && !isFacesheetsSelected) {
    // HIE only forces Facesheets when this profile has HIE-capable templates.
    if (capabilities.hasHieTemplate) {
      return {
        isValid: false,
        errorMessage:
          FACESHEETS_VALIDATION_MESSAGES.FACESHEETS_REQUIRED_FOR_HIE,
        missingMethods: [RetrievalMethod.FACESHEETS],
      };
    }
  }

  if (!isFacesheetsSelected) return { isValid: true };

  if (capabilities.hasHieTemplate && !capabilities.hasFullTemplate) {
    if (isHieSelected || allowStandaloneFacesheets) return { isValid: true };
    return {
      isValid: false,
      errorMessage: FACESHEETS_VALIDATION_MESSAGES.HIE_REQUIRED,
      missingMethods: [RetrievalMethod.HIE],
    };
  }

  if (capabilities.hasFullTemplate && !capabilities.hasHieTemplate) {
    if (isProviderFacilitySelected || allowStandaloneFacesheets)
      return { isValid: true };
    return {
      isValid: false,
      errorMessage: FACESHEETS_VALIDATION_MESSAGES.FULL_REQUIRED,
      missingMethods: [RetrievalMethod.PROVIDER_FACILITY],
    };
  }

  if (capabilities.hasFullTemplate && capabilities.hasHieTemplate) {
    if (hasRetrievalSelection) return { isValid: true };
    if (allowStandaloneFacesheets) {
      return { isValid: true };
    }
    return {
      isValid: false,
      errorMessage: FACESHEETS_VALIDATION_MESSAGES.FULL_OR_HIE_REQUIRED,
      missingMethods: [
        RetrievalMethod.PROVIDER_FACILITY,
        RetrievalMethod.HIE,
      ].filter((method) => !selectedMethods.includes(method)),
    };
  }

  return { isValid: true };
};

export const getAllowedFacesheets = ({
  selectedMethods,
  facesheets,
  allowStandaloneFacesheets = false,
}: GetAllowedFacesheetsParams): Facesheet[] => {
  const isFacesheetsSelected = selectedMethods.includes(
    RetrievalMethod.FACESHEETS,
  );
  const isHieSelected = selectedMethods.includes(RetrievalMethod.HIE);
  const isProviderFacilitySelected = selectedMethods.includes(
    RetrievalMethod.PROVIDER_FACILITY,
  );

  if (!isFacesheetsSelected) return [];

  if (isHieSelected && isProviderFacilitySelected) {
    return facesheets.filter(
      (facesheet) =>
        hasTemplateType(facesheet, 'HIE') || hasTemplateType(facesheet, 'FULL'),
    );
  }

  if (isHieSelected) {
    return facesheets.filter((facesheet) => hasTemplateType(facesheet, 'HIE'));
  }

  if (isProviderFacilitySelected) {
    return facesheets.filter((facesheet) => hasTemplateType(facesheet, 'FULL'));
  }

  if (allowStandaloneFacesheets) {
    return facesheets.filter((facesheet) => hasTemplateType(facesheet, 'FULL'));
  }

  return [];
};

export const isFacesheetAllowed = ({
  selectedMethods,
  facesheet,
  allowStandaloneFacesheets = false,
}: IsFacesheetAllowedParams): boolean =>
  getAllowedFacesheets({
    selectedMethods,
    facesheets: [facesheet],
    allowStandaloneFacesheets,
  }).length > 0;
